import React, { useEffect, useState } from "react";
import { Button, Card, Form, Modal, Spinner } from "react-bootstrap";
import { FiBarChart2 } from "react-icons/fi";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { ErrorResponse } from "@core/types/errorResponse";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { useFinanceAllocationRuleAction } from "@features/admin/finance/allocation-rule/hooks/useFinanceAllocationRuleAction";
import { useFinanceCategoryOptions } from "@features/admin/finance/category/hooks/useFinanceCategoryOptions";
import { FinanceAllocationRule } from "@features/admin/finance/allocation-rule/types/financeAllocationRule";
import { UpdateFinanceAllocationRuleRequest } from "@features/admin/finance/allocation-rule/types/financeAllocationRuleRequests";
import InfoTooltip from "@core/components/InfoTooltip";
import StatusPill from "@core/components/StatusPill";

type FieldErrors = Partial<Record<string, string[]>>;
type DistributionPrefix = "minister" | "head_office" | "district";

const EMPTY_FORM: UpdateFinanceAllocationRuleRequest = {
    rule_name: "",
    rule_description: "",
    minister_calc_type: "",
    minister_rate: "",
    minister_cap: "",
    minister_fixed: "",
    head_office_calc_type: "",
    head_office_rate: "",
    head_office_cap: "",
    head_office_fixed: "",
    district_calc_type: "",
    district_rate: "",
    district_cap: "",
    district_fixed: "",
};

const UpdateAllocationRuleModal: React.FC<{
    show: boolean;
    onHide: () => void;
    rule: FinanceAllocationRule | null;
    onSuccess?: () => void | Promise<void>;
}> = ({ show, onHide, rule, onSuccess }) => {
    const resolvedNationId = useScopeId() ?? 0;
    const [form, setForm] = useState<UpdateFinanceAllocationRuleRequest>(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState<FieldErrors>({});
    const showError = useApiError();
    const showValidation = useErrorModalStore((s) => s.showValidation);
    const { busy, doUpdate } = useFinanceAllocationRuleAction();
    const { calcTypes, calcTypesLoading, calcTypesError, fetchCalcTypes } = useFinanceCategoryOptions();

    useEffect(() => { fetchCalcTypes(); }, [fetchCalcTypes]);

    useEffect(() => {
        if (rule) {
            setForm({
                rule_name: rule.rule_name ?? "",
                rule_description: rule.rule_description ?? "",
                minister_calc_type: rule.minister?.calc_type ?? "",
                minister_rate: rule.minister?.rate !== null ? String(rule.minister?.rate ?? "") : "",
                minister_cap: rule.minister?.cap !== null ? String(rule.minister?.cap ?? "") : "",
                minister_fixed: rule.minister?.fixed !== null ? String(rule.minister?.fixed ?? "") : "",
                head_office_calc_type: rule.head_office?.calc_type ?? "",
                head_office_rate: rule.head_office?.rate !== null ? String(rule.head_office?.rate ?? "") : "",
                head_office_cap: rule.head_office?.cap !== null ? String(rule.head_office?.cap ?? "") : "",
                head_office_fixed: rule.head_office?.fixed !== null ? String(rule.head_office?.fixed ?? "") : "",
                district_calc_type: rule.district?.calc_type ?? "",
                district_rate: rule.district?.rate !== null ? String(rule.district?.rate ?? "") : "",
                district_cap: rule.district?.cap !== null ? String(rule.district?.cap ?? "") : "",
                district_fixed: rule.district?.fixed !== null ? String(rule.district?.fixed ?? "") : "",
            });
        } else {
            setForm(EMPTY_FORM);
        }
        setFormErrors({});
    }, [rule]);

    const handleClose = () => {
        setFormErrors({});
        onHide();
    };

    const handleChange = (field: keyof UpdateFinanceAllocationRuleRequest) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const value = e.target.value;
        setForm((f) => ({ ...f, [field]: value }));
        if (formErrors[field as string]) {
            setFormErrors((prev) => ({ ...prev, [field]: undefined }));
        }
        if (field === "minister_calc_type") {
            setForm((f) => ({ ...f, minister_calc_type: value, minister_rate: "", minister_cap: "", minister_fixed: "" }));
        }
        if (field === "head_office_calc_type") {
            setForm((f) => ({ ...f, head_office_calc_type: value, head_office_rate: "", head_office_cap: "", head_office_fixed: "" }));
        }
        if (field === "district_calc_type") {
            setForm((f) => ({ ...f, district_calc_type: value, district_rate: "", district_cap: "", district_fixed: "" }));
        }
    };

    const handleSubmit = async () => {
        if (!rule) return;

        const errors: FieldErrors = {};
        const messages: string[] = [];

        if (!form.rule_name?.trim()) {
            errors.rule_name = ["Rule name is required."];
            messages.push("Rule name is required.");
        }

        if (messages.length > 0) {
            setFormErrors(errors);
            showValidation(messages);
            return;
        }

        try {
            const payload: UpdateFinanceAllocationRuleRequest = {
                ...form,
                rule_description: form.rule_description || undefined,
                minister_calc_type: form.minister_calc_type || undefined,
                minister_rate: form.minister_rate || undefined,
                minister_cap: form.minister_cap || undefined,
                minister_fixed: form.minister_fixed || undefined,
                head_office_calc_type: form.head_office_calc_type || undefined,
                head_office_rate: form.head_office_rate || undefined,
                head_office_cap: form.head_office_cap || undefined,
                head_office_fixed: form.head_office_fixed || undefined,
                district_calc_type: form.district_calc_type || undefined,
                district_rate: form.district_rate || undefined,
                district_cap: form.district_cap || undefined,
                district_fixed: form.district_fixed || undefined,
            };

            const response = await doUpdate(resolvedNationId, rule.rule_group_id, payload);

            if (response.isSuccess()) {
                showSuccess(response.getData()?.message ?? '');
                handleClose();
                await onSuccess?.();
            } else {
                const error = response.getError() as ErrorResponse;
                const fieldErrors = (error?.errors as FieldErrors) ?? {};
                setFormErrors(fieldErrors);
                const list = (Object.values(fieldErrors).flat().filter(Boolean) as string[]);
                showValidation(list.length ? list : [error?.detail || "Failed to update allocation rule."]);
                showError(error);
            }
        } catch (err) {
            showError(err as ErrorResponse);
        }
    };

    const renderDistributionSection = (prefix: DistributionPrefix, label: string) => {
        const calcTypeField = `${prefix}_calc_type` as keyof UpdateFinanceAllocationRuleRequest;
        const rateField = `${prefix}_rate` as keyof UpdateFinanceAllocationRuleRequest;
        const capField = `${prefix}_cap` as keyof UpdateFinanceAllocationRuleRequest;
        const fixedField = `${prefix}_fixed` as keyof UpdateFinanceAllocationRuleRequest;

        const calcType = form[calcTypeField] as string;
        const showRate = calcType === "PERCENTAGE" || calcType === "PERCENTAGE_WITH_CAP";
        const showCap = calcType === "PERCENTAGE_WITH_CAP";
        const showFixed = calcType === "FIXED_AMOUNT";

        return (
            <Card className="mb-4 border-0 bg-light" key={prefix}>
                <Card.Body>
                    <h6 className="fw-semibold mb-3 text-muted text-uppercase small">{label}</h6>
                    <Form.Group className="mb-3" controlId={`update_${prefix}_calc_type`}>
                        <Form.Label>
                            Calculation Type
                            <InfoTooltip text={`How income is calculated for the ${label.toLowerCase()}. Leave blank if no allocation applies.`} />
                        </Form.Label>
                        {calcTypesLoading ? (
                            <div className="d-flex align-items-center"><Spinner animation="border" size="sm" className="me-2" /><span>Loading…</span></div>
                        ) : calcTypesError ? (
                            <div className="text-danger small">Failed to load calculation types.</div>
                        ) : (
                            <Form.Select
                                value={form[calcTypeField] as string ?? ""}
                                onChange={handleChange(calcTypeField)}
                                isInvalid={!!formErrors[calcTypeField as string]}
                            >
                                <option value="">None (no allocation)</option>
                                {calcTypes.map((ct) => (
                                    <option key={ct.calc_type_code} value={ct.calc_type_code}>
                                        {ct.calc_type_name}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                        <Form.Control.Feedback type="invalid">{formErrors[calcTypeField as string]?.[0]}</Form.Control.Feedback>
                    </Form.Group>

                    {showRate && (
                        <Form.Group className="mb-3" controlId={`update_${prefix}_rate`}>
                            <Form.Label>Rate (%) <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="number" placeholder="e.g. 10" min="0" max="100" step="0.01"
                                value={form[rateField] as string ?? ""} onChange={handleChange(rateField)}
                                isInvalid={!!formErrors[rateField as string]}
                            />
                            <Form.Control.Feedback type="invalid">{formErrors[rateField as string]?.[0]}</Form.Control.Feedback>
                        </Form.Group>
                    )}
                    {showCap && (
                        <Form.Group className="mb-3" controlId={`update_${prefix}_cap`}>
                            <Form.Label>Cap Amount <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="number" placeholder="e.g. 500.00" min="0.01" step="0.01"
                                value={form[capField] as string ?? ""} onChange={handleChange(capField)}
                                isInvalid={!!formErrors[capField as string]}
                            />
                            <Form.Control.Feedback type="invalid">{formErrors[capField as string]?.[0]}</Form.Control.Feedback>
                        </Form.Group>
                    )}
                    {showFixed && (
                        <Form.Group className="mb-3" controlId={`update_${prefix}_fixed`}>
                            <Form.Label>Fixed Amount <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="number" placeholder="e.g. 200.00" min="0.01" step="0.01"
                                value={form[fixedField] as string ?? ""} onChange={handleChange(fixedField)}
                                isInvalid={!!formErrors[fixedField as string]}
                            />
                            <Form.Control.Feedback type="invalid">{formErrors[fixedField as string]?.[0]}</Form.Control.Feedback>
                        </Form.Group>
                    )}
                </Card.Body>
            </Card>
        );
    };

    return (
        <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} centered size="lg" data-testid="update-allocation-rule-modal">
            <Modal.Header closeButton className="border-0 pb-0">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <Modal.Title className="fw-semibold mb-0">
                        <FiBarChart2 className="me-2" aria-hidden />Edit Allocation Rule
                    </Modal.Title>
                    {rule && <StatusPill active={rule.is_active} />}
                </div>
            </Modal.Header>
            <Modal.Body className="pt-3" style={{ maxHeight: "75vh", overflowY: "auto" }}>
                <Form>
                    {/* Section 1: Basic Info */}
                    <Card className="mb-4 border-0 bg-light">
                        <Card.Body>
                            <h6 className="fw-semibold mb-3 text-muted text-uppercase small">Basic Info</h6>
                            <Form.Group className="mb-3" controlId="update_rule_name">
                                <Form.Label>Rule Name <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter rule name"
                                    value={form.rule_name}
                                    onChange={handleChange("rule_name")}
                                    isInvalid={!!formErrors.rule_name}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.rule_name?.[0]}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-0" controlId="update_rule_description">
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    placeholder="Enter description (optional)"
                                    value={form.rule_description ?? ""}
                                    onChange={handleChange("rule_description")}
                                    isInvalid={!!formErrors.rule_description}
                                />
                                <Form.Control.Feedback type="invalid">{formErrors.rule_description?.[0]}</Form.Control.Feedback>
                            </Form.Group>
                        </Card.Body>
                    </Card>

                    {/* Section 2: KPI (read-only on edit) */}
                    <Card className="mb-4 border-0 bg-light">
                        <Card.Body>
                            <h6 className="fw-semibold mb-3 text-muted text-uppercase small">
                                KPI
                                <InfoTooltip text="The KPI cannot be changed after the rule is created." />
                            </h6>
                            <Form.Group className="mb-0" controlId="update_kpi_id">
                                <Form.Label visuallyHidden>KPI</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={rule?.kpi_name_snapshot ?? "—"}
                                    readOnly
                                    disabled
                                    className="bg-white"
                                />
                            </Form.Group>
                        </Card.Body>
                    </Card>

                    {/* Distribution Sections */}
                    {renderDistributionSection("minister", "Minister Distribution")}
                    {renderDistributionSection("head_office", "Head Office Distribution")}
                    {renderDistributionSection("district", "District Distribution")}

                    <div className="d-flex justify-content-end mt-2">
                        <Button variant="primary" size="lg" className="btn-action" onClick={handleSubmit} disabled={busy} aria-busy={busy}>
                            {busy ? (
                                <><Spinner animation="border" size="sm" className="me-2" />Updating…</>
                            ) : (
                                "Update Rule"
                            )}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default UpdateAllocationRuleModal;
